/**
 * JobAuto Gehaltscheck — pure calculation helpers.
 *
 * Kept free of React so the Mindestgehalt logic is unit-testable in isolation.
 * The check is a GROSS-salary floor: the employee's salary is taken gross, the
 * JobAuto salary conversion and all other existing conversions are deducted
 * from the annual gross salary, and the remainder is compared against the
 * internal minimum threshold.
 *
 * IMPORTANT — the JobAuto conversion is NET of VAT: FINN is
 * vorsteuerabzugsberechtigt and covers the input VAT, so the amount actually
 * converted from the employee's gross salary is the *net* Umwandlungsrate, not
 * the portal's gross (VAT-incl.) rate. Confirmed by People (Sarah/Sofia,
 * 2026-07-08). The gross vs. net distinction here is about VAT — it is
 * unrelated to gross-vs-net *salary* (social contributions / wage tax), which
 * is why the salary side stays gross while the car rate is netted.
 */

/** Monthly employee cost of one existing company car (salary conversion + GwV). */
export interface ExistingCarDeduction {
  eigenanteil: number
  gwv: number
}

export interface ExistingDeductionsInput {
  bavAN: number
  firmenfitness: number
  fahrradGehaltsumwandlung: number
  /** Per-car deductions parsed from the payslip (authoritative when present). */
  cars: ExistingCarDeduction[]
  /** Indices in `cars` excluded from the calc (e.g. a car returned in a swap). */
  excludedCarIndices?: ReadonlySet<number>
  /** Flat car fallback used only when no per-car data was parsed. */
  carGehaltsumwandlungFlat: number
  carGwvFlat: number
}

/** Monthly total of all existing salary conversions (excluding the new JobAuto). */
export function computeExistingMonthly(input: ExistingDeductionsInput): number {
  const excluded = input.excludedCarIndices ?? new Set<number>()
  const carMonthly =
    input.cars.length > 0
      ? input.cars.reduce(
          (sum, car, i) => (excluded.has(i) ? sum : sum + car.eigenanteil + car.gwv),
          0,
        )
      : input.carGehaltsumwandlungFlat + input.carGwvFlat
  return input.bavAN + input.firmenfitness + input.fahrradGehaltsumwandlung + carMonthly
}

export interface EligibilityInput {
  bruttoMonatlich: number
  monateImJahr: number
  /** Net (ex-VAT) monthly salary conversion of the new JobAuto being checked. */
  neuesJobautoRate: number
  /** Monthly total of all other existing conversions (see computeExistingMonthly). */
  existingMonthly: number
  mindestgrenze: number
}

export interface EligibilityResult {
  jahresBruttoMitJobAuto: number
  belowMindestgehalt: boolean
  ok: boolean
}

export function computeEligibility(input: EligibilityInput): EligibilityResult {
  const jahresBruttoMitJobAuto =
    input.bruttoMonatlich * 12 -
    input.existingMonthly * 12 -
    input.neuesJobautoRate * input.monateImJahr
  const belowMindestgehalt =
    input.bruttoMonatlich > 0 && jahresBruttoMitJobAuto < input.mindestgrenze
  return {
    jahresBruttoMitJobAuto,
    belowMindestgehalt,
    ok: input.bruttoMonatlich > 0 && !belowMindestgehalt,
  }
}

/**
 * Assumed monthly bAV employee contribution when the payslip shows none.
 * Sofia (People) requested a conservative 430 €/mo fallback so the floor check
 * isn't overly optimistic; it is always flagged as an assumption in the UI.
 */
export const BAV_ASSUMPTION_MONTHLY = 430

/** Default internal minimum gross annual salary after all conversions. */
export const DEFAULT_MINDESTGRENZE_ANNUAL = 40_000

/**
 * German VAT rate applied to JobAuto subscription rates. The portal quotes the
 * gross (VAT-incl.) monthly rate, but the salary conversion uses the net rate
 * (see module doc) — so the gross rate is divided by (1 + VAT_RATE).
 */
export const VAT_RATE = 0.19

/**
 * Convert a gross (VAT-incl.) JobAuto rate to the net Umwandlungsrate that is
 * actually deducted from the employee's gross salary. Returns 0 for
 * non-positive / non-finite input so a missing rate never poisons the floor.
 */
export function bruttoToNettoRate(brutto: number): number {
  if (!Number.isFinite(brutto) || brutto <= 0) return 0
  return brutto / (1 + VAT_RATE)
}
